import { useLayoutEffect } from "react";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import DatePicker from "components/v2/DatePicker/DatePicker";
import Input from "components/v2/Input/Input";
import Checkbox from "components/v2/Checkbox/Checkbox";
import Alert from "components/v2/Alert/Alert";
import TimeInput from "components/v2/TimeInput/TimeInput";
import testIds from "constants/testIds";
import { DELAY_REASON_OPTIONS } from "../pickupDeliveryProofOptions";
import ProofOfDeliveryAttachment from "./ProofOfDeliveryAttachment";
import { getDateFromOffset } from "../utils/podFormUtils";

export type ProofOfDeliverySharedFieldsProps = Readonly<{
  quotedDeliveryDate?: string;
  quotedDeliveryTime?: string;
  timezoneOffset: string;
  showBillingStatus?: boolean;
  showCompletionWarning?: boolean;
  /** Use gap-based spacing when the parent already stacks sections (e.g. multistop POD dialog). */
  layout?: "margins" | "stacked";
}>;

export default function ProofOfDeliverySharedFields({
  quotedDeliveryDate,
  quotedDeliveryTime,
  timezoneOffset,
  showBillingStatus = false,
  showCompletionWarning = true,
  layout = "margins",
}: ProofOfDeliverySharedFieldsProps) {
  const { watch, register, setValue, getValues } = useFormContext();
  const intl = useIntl();
  const isStacked = layout === "stacked";
  const sectionClassName = isStacked ? undefined : "mt-3xl";
  const alertClassName = isStacked ? undefined : "mt-lg mb-xxs";

  const deliveryDate = watch("delivery_date");
  const deliveryTime = watch("delivery_time");

  // Default to unpaid via setValue rather than defaultChecked: a defaultChecked radio makes
  // react-hook-form re-read the DOM on remount, which would discard a previously picked value.
  // Layout effect so the radios are never painted with nothing selected when the dialog
  // remounts these fields.
  useLayoutEffect(() => {
    if (!showBillingStatus || getValues("billing_status")) {
      return;
    }
    setValue("billing_status", "unpaid");
  }, [showBillingStatus, getValues, setValue]);

  const actualDeliveryMoment = moment(`${quotedDeliveryDate} ${quotedDeliveryTime}`, "YYYY-MM-DD HH:mm");
  const expectedDeliveryMoment = moment(`${deliveryDate} ${deliveryTime}`, "YYYY-MM-DD HH:mm");
  const isDelayed =
    quotedDeliveryDate &&
    quotedDeliveryTime &&
    deliveryDate &&
    deliveryTime &&
    actualDeliveryMoment.isBefore(expectedDeliveryMoment);

  const fields = (
    <>
      <Row className={sectionClassName}>
        <Col md="6">
          <div className="text-sm text-secondary-v2 font-medium mb-sm">
            <FormattedMessage id="components.molecules.top-nav.deliveryDate" defaultMessage="Delivery date" />
            <span className="text-danger ml-xss">*</span>
          </div>
          <DatePicker
            name="delivery_date"
            selectionMode="single"
            alignment="right"
            numberOfMonths={1}
            maxDate={getDateFromOffset(timezoneOffset)}
            required
          />
        </Col>
        <Col md="6">
          <div className="text-sm text-secondary-v2 font-medium mb-sm">
            <FormattedMessage id="components.molecules.top-nav.deliveryTime" defaultMessage="Delivery time" />
            <span className="text-danger ml-xss">*</span>
          </div>
          <TimeInput name="delivery_time" required />
        </Col>
      </Row>
      {isDelayed && (
        <Row className={sectionClassName}>
          <Col md="12">
            <div className="text-sm text-secondary-v2 font-medium mb-sm">
              <FormattedMessage id="components.molecules.top-nav.reasonForDelay" defaultMessage="Reason for delay" />
            </div>
            <div className="pod-container__delay-options">
              {DELAY_REASON_OPTIONS.map((delayReason) => (
                <Checkbox
                  key={delayReason.value}
                  label={delayReason.label}
                  name={`delay_reason.${delayReason.value}`}
                  size="md"
                />
              ))}
            </div>
          </Col>
        </Row>
      )}
      <Row className={sectionClassName}>
        <Col md="12">
          <Input
            label={intl.formatMessage({
              id: "components.molecules.top-nav.nameOfAcceptor",
              defaultMessage: "Name of acceptor",
            })}
            name="acceptor_name"
            required
          />
        </Col>
      </Row>
      <Row className={sectionClassName}>
        <Col md="12">
          <ProofOfDeliveryAttachment />
        </Col>
      </Row>
      {showBillingStatus && (
        <Row className={sectionClassName} data-testid={testIds.SHIPMENT_DASHBOARD_POD_BILLING_STATUS}>
          <Col md="12">
            <div className="text-sm text-secondary-v2 font-medium mb-sm">
              <FormattedMessage id="components.molecules.top-nav.billingStatus" defaultMessage="Billing status" />
              <span className="text-danger ml-xss">*</span>
            </div>
            <div className="d-flex flex-column gap-sm">
              <label className="d-flex align-items-center">
                <span className="text-md">
                  <input
                    type="radio"
                    value="unpaid"
                    {...register("billing_status", { required: true })}
                    data-testid={testIds.SHIPMENT_DASHBOARD_POD_BILLING_STATUS_UNPAID}
                    className="mr-sm"
                  />
                  <FormattedMessage
                    id="components.molecules.top-nav.billingStatus.unpaid"
                    defaultMessage="Unpaid, invoice submission required"
                  />
                </span>
              </label>
              <label className="d-flex align-items-center">
                <span className="text-md">
                  <input
                    type="radio"
                    value="paid"
                    {...register("billing_status", { required: true })}
                    data-testid={testIds.SHIPMENT_DASHBOARD_POD_BILLING_STATUS_PAID}
                    className="mr-sm"
                  />
                  <FormattedMessage
                    id="components.molecules.top-nav.billingStatus.paid"
                    defaultMessage="Paid, no invoice submission required"
                  />
                </span>
              </label>
            </div>
          </Col>
        </Row>
      )}
      {showCompletionWarning && (
        <Row className={alertClassName}>
          <Col md="12">
            <Alert variant="brand" label="">
              <FormattedMessage
                id="components.molecules.top-nav.submittingWarningText"
                defaultMessage="Submitting this form will mark this shipment as complete."
              />
            </Alert>
          </Col>
        </Row>
      )}
    </>
  );

  if (isStacked) {
    return <div className="d-flex flex-column gap-3xl">{fields}</div>;
  }

  return fields;
}
